import {RolandStructuredObject} from "./RolandStructuredObject.js";
import {RolandStructuredVariableArray} from "./RolandStructuredArray.js";

/**
 * Base class for objects that represent the offset and length of a chunk of data,
 * appended to the end of a RolandVariableArrayWithAppendedData
 */
export class OffsetAndLengthObject extends RolandStructuredObject {
    constructor(file, data, parent, start, length, description) {
        super(file, data, parent, start, length, description);
    }

    offsetBytePosition=undefined;
    lengthBytePosition=undefined;

    getOffset() {
        return this.getUint32(this.offsetBytePosition);
    }

    getLength() {
        return this.getUint32(this.lengthBytePosition);
    }

    setOffset(offset, length) {
        this.setInt32(this.offsetBytePosition, offset);
    }

    setLength(length) {
        this.setInt32(this.lengthBytePosition, length);
    }

}

/**
 * An array of objects, variable in size with a kind of broken header, and data appended to the end of the array . MC LPDa uses this.
 * The array item length is repurposed as the size of all the data (not including the header) item count is correct.
 * It is assumed that the array item contain a reference to the data that sits after the array.
 * 
 * childObjectLength is the size of each in the array portion, and will be need to be set during child class construction.
 * 
 * Child items must derive from OffsetAndLengthObject
 * 
 * children will only contain the array items, the appended data will be in appendedDataChildren
 * childObjectCount will be the number of array items
 * Only the header size + array items will be owned by this object
 * 
 * when modifying the appended data, just call setLength on the OffsetAndLengthObject. Offsets
 * are calulated by the setOffsets method, called during calculateLength. Before that point, offset and 
 * length of the array/chunk are not known.
 */
export class RolandVariableArrayWithAppendedData extends RolandStructuredVariableArray {
    /**
     *
     * @param file
     * @param data
     * @param start
     * @param length
     * @param parent
     * @param type
     * @param childArrayTypes if type is of an array, this is the child of that array
     */
    constructor(file, data,parent,start,length,type,...childArrayTypes) {
        super(file,data,parent,start,length,type,...childArrayTypes);
    }

    appendedDataChildren=[];
    appendedDataChildType=undefined;

    /**
     * Load the children of the array, THEN the appended data
     * @param {*} lc 
     */
    nestedLoad(lc) {
        if (!this.childObjectLength)
            throw "childObjectLength must be set";
        this.childObjectCount = this.getUint32(0);        
        this.headerSize = this.getUint32(8);
        // we will only own the header size 
        this.ownedLength = this.headerSize;

        // load the array children first
        let dataPtr = super.loadArrayChildren(lc);

        // walk the children and get the offset and length of the appended data
        for(let i=0; i<this.childObjectCount; i++) {
            let pointer = this.children[i];
            let length = pointer.getLength();
            if (length===0)
                continue;    // no data to append
            let offset = pointer.getOffset();
            let appendedDataStart = this.start + this.headerSize + offset;
            console.log(`Appended data start: ${appendedDataStart} ${appendedDataStart.toString(16)}`);
            let data = new this.appendedDataChildType(this.file, this.data, this, appendedDataStart, length);
            data.nestedLoad(lc);
            this.appendedDataChildren.push(data);

            console.log(`Appended data: ${data.description} ${data.start} ${data.start.toString(16)} ${data.length} ${data.length.toString(16)}`);
        // write first 16 bytes of data in hex
        let hexBytes = '';
        for (let j = 0; j < Math.min(length, 16); j++) {
            let byte = data.getUint8(j);
            hexBytes += byte.toString(16).padStart(2, '0') + ' ';
        }
        hexBytes = hexBytes.trim();
        console.log(`First 16 bytes (hex): ${hexBytes}`);
        }
    }

    /**
     * Set the offsets of the appended data children and return the total length of the appended data
     */
    setOffsets() {
        let totalLength = 0;
        let offset = this.childObjectLength*this.childObjectCount;
        let appendedDataIndex = 0;

        // walk the children (contains offset and length)
        for(let i=0; i<this.children.length; i++) {
            let child = this.children[i];
            child.setOffset(offset);
            let length = child.getLength();
            if (length>0) {
                let appendedData = this.appendedDataChildren[appendedDataIndex++];
                console.log(`setOffsets:Appended data: 0x${appendedData.start.toString(16)} 0x${appendedData.length.toString(16)} ${appendedData.length}`);
                console.assert(appendedData.length === length, "Appended data length does not match OffsetAndLengthObject length");
                totalLength += length;
                offset += length;
            } 
        }
        return totalLength;
    }

    /**
     * 
     * @param {Number} destination 
     * @returns 
     */
    calculateLength(destination) {
        this.calculatedNewPosition = destination;
        destination+=this.ownedLength

        let appendedDataLength = this.setOffsets();        

        // we don't need to calculate lengths of the children, they are not going to be changed.
        // chunkDataLength is all the children and the appended data
        let chunkDataLength = this.childObjectLength*this.childObjectCount + appendedDataLength;
        this.setItemLength(chunkDataLength);
        this.length = chunkDataLength + this.headerSize;

        return this.length;
    }

    /**
     * nested function to copy this object's owned length, then all its children to a new DataView
     * @param destination
     * @param dv
     * @return new destination, i.e. after this object has been written
     */
    writeDataToView( destination, dv) {
        // this will write the array header and the array children
        destination = super.writeDataToView(destination, dv);

        // write the appended data
        this.appendedDataChildren.forEach( (child) => {
            console.log(`Writing appended data: ${child.description} ${child.start} ${child.start.toString(16)} ${child.length} ${child.length.toString(16)} destination: ${destination} ${destination.toString(16)}`);
            destination = child.writeDataToView( destination, dv );
        })
        return destination;
    }
}